import {
  ApiLanderRequestPayload,
  ApiLocation,
  ApiProject,
  ApiReadLanderResponse,
  LocationItemLocationItemCondition,
  LocationItemLocationItemType,
} from "@incendium/api";
import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { landersService, locationService } from "Apis";
import { getPathFromUrl } from "Helpers";
import { useLocations, useNotification } from "Hooks";
import { useSubdomains } from "Hooks/useSubdomains";
import produce from "immer";
import { Suspense, useCallback, useMemo, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useDebounce, useUpdateEffect } from "react-use";
import { Link as RouterLink } from "react-router-dom";
import { SubdomainSelector } from "features/subdomains";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import { useLightningLanderContext } from "features/lightningLanders/providers/LightningLanderProvider";
import SmartLinksSection from "./SmartLinksSection";
import { buildLandingUrl } from "features/lightningLanders/utils";
import { getRootDomain } from "features/subdomains/utils";

interface ILightningLanderFormProps {
  project: ApiProject;
  onSaved: (v: ApiReadLanderResponse) => void;
}

function LightningLanderForm({ project, onSaved }: ILightningLanderFormProps) {
  const { lander, setLander, component, setComponent } =
    useLightningLanderContext();
  const { subdomains } = useSubdomains();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [locationName, setLocationName] = useState("");
  const [validatedURL, setValidatedURL] = useState("");
  const [matchingLocations, setMatchingLocations] = useState<ApiLocation[]>([]);
  const { setLocations } = useLocations();

  const onSubmit = useCallback(async () => {
    const payload: ApiLanderRequestPayload = {
      description: lander.description,
      path: lander.path,
      subdomainId: lander.subdomainId,
      componentId: component.id,
      componentName: component.name,
      noIndex: lander.noIndex,
      canonical: lander.canonical,
      deferIncendiumjs: lander.deferIncendiumjs,
    };

    try {
      const fn = lander.id
        ? landersService.landersServiceUpdateLander({
            projectId: project.id as number,
            landerId: lander.id,
            payload,
          })
        : landersService.landersServiceCreateLander({
            projectId: project.id as number,
            payload,
          });
      const res = await fn;
      onSaved(res);
      showSuccessNotification(`${lander.path} Saved!`);
    } catch (error) {
      showErrorNotification("Internal Error - Failed to save Lander");
    }
  }, [
    showSuccessNotification,
    showErrorNotification,
    lander,
    project,
    onSaved,
    component,
  ]);

  const subDomainObj = useMemo(() => {
    return lander.subdomainId
      ? subdomains.find((s) => s.id === lander.subdomainId)
      : null;
  }, [lander, subdomains]);

  const domain = useMemo(() => {
    return subDomainObj?.domain
      ? `.${getRootDomain(subDomainObj?.domain || "")}`
      : "[not-set]";
  }, [subDomainObj]);

  const subDomain = useMemo(() => {
    return subDomainObj ? subDomainObj.subdomain : "[not-set]";
  }, [subDomainObj]);

  const fullPath = useMemo(() => {
    return buildLandingUrl(subDomainObj, lander.path, true);
  }, [subDomainObj, lander.path]);

  useDebounce(
    () => {
      let url = validatedURL && validatedURL !== "" ? validatedURL : fullPath;

      const fn = async () => {
        const res =
          await locationService.locationServiceGetProjectsLocationsByPage({
            projectKey: `${project.key}`,
            queryPage: url,
          });

        setMatchingLocations(res.matching || []);
      };
      if (project && !url.includes("[not-set]")) {
        fn();
      }
    },
    500,
    [fullPath, project, validatedURL],
  );

  const validateURL = useCallback(async (url) => {
    const res = await locationService.locationServiceValidateLocation({
      body: {
        url,
      },
    });
    if (res && res.newUrl && res.newUrl !== "") {
      setValidatedURL(res.newUrl);
    }
  }, []);

  useUpdateEffect(() => {
    if (fullPath.includes("not-set")) {
      return;
    }
    validateURL(fullPath);
  }, [fullPath]);

  const createLocation = useCallback(async () => {
    try {
      let fullUrl =
        validatedURL && validatedURL !== "" ? validatedURL : fullPath;
      let path = getPathFromUrl(fullUrl);

      const location: ApiLocation = {
        name: locationName,
        urlMatch: fullUrl,
        showInAnalytics: true,
        items: [
          {
            type: LocationItemLocationItemType.DOMAIN,
            condition: LocationItemLocationItemCondition.EQUAL,
            value: `${subDomain}${domain}`,
          },
          {
            type: LocationItemLocationItemType.PATH,
            condition: LocationItemLocationItemCondition.EQUAL,
            value: path,
          },
        ],
      };

      const res = await locationService.locationServiceCreateLocation({
        projectId: project.id as number,
        payload: { location },
      });

      setMatchingLocations(
        produce(matchingLocations, (draft) => {
          draft.push(res.result!);
        }),
      );
      setLocations((locations) =>
        produce(locations, (draft) => {
          draft.push(res.result!);
        }),
      );
      setLocationName("");
      showSuccessNotification(`${res.result?.name || ""} Saved!`);
    } catch (error) {
      showErrorNotification("Internal Error - Failed to save Location");
    }
  }, [
    project,
    fullPath,
    locationName,
    domain,
    subDomain,
    showSuccessNotification,
    showErrorNotification,
    matchingLocations,
    validatedURL,
    setLocations,
  ]);
  return (
    <ValidatorForm onSubmit={onSubmit}>
      <Suspense>
        <AccordianChartBuilderSidebarBlock
          title="General Settings"

          // defaultClosed
        >
          <Typography variant="body2">
            Configure the Path and Subdomain for your Landing Page. If you
            haven't created the required subdomain, you can do so{" "}
            <Link
              underline="always"
              to={`/clients/${project.clientId}/projects/${project.id}/connect/subdomains`}
              color="inherit"
              component={RouterLink}
            >
              here
            </Link>
            .
          </Typography>
          <Stack mt={3} spacing={3}>
            <TextValidator
              variant="outlined"
              fullWidth
              size="small"
              name="name"
              label="Name"
              helperText="Internal name for this landing page, note if seo title not set this will be your pages title"
              value={component.name || ""}
              validators={[
                "required",
                "minStringLength:3",
                "maxStringLength:200",
              ]}
              errorMessages={[
                "Name is required.",
                "Name must be at least 3 characters.",
                "Name must be at most 200 characters.",
              ]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setComponent(
                  produce(component, (draft) => {
                    draft.name = e.target.value;
                  }),
                )
              }
            />
            <TextValidator
              variant="outlined"
              fullWidth
              size="small"
              name="path"
              label="Landing Path"
              helperText="do not include the initial /"
              value={lander.path || ""}
              validators={["isPath", "required"]}
              errorMessages={[
                "Landing Path must be a valid path and not start with /",
                "Landing Path is required.",
              ]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLander(
                  produce(lander, (draft) => {
                    draft.path = e.target.value;
                  }),
                )
              }
            />
            <SubdomainSelector
              subdomainId={lander.subdomainId}
              setSubdomainId={(v) =>
                setLander(
                  produce(lander, (draft) => {
                    draft.subdomainId = v;
                  }),
                )
              }
              helperText={`This is the subdomain people will access to use your landing page. e.g myselecteddomain${domain}`}
            />

            <Alert
              severity={
                lander.subdomainId && lander.path ? "success" : "warning"
              }
            >
              <Typography variant="subtitle2" color={"secondary"}>
                Your page will be available at{" "}
              </Typography>
              <Link href={fullPath} target="_blank" color={"primary.main"}>
                {fullPath}
              </Link>
            </Alert>
            <Divider />
            <TextField
              variant="outlined"
              fullWidth
              name="description"
              label="Description"
              helperText="Provide your Lander with a description to clarify its purpose and enhance understanding of the page's objectives."
              value={lander.description}
              multiline
              rows={2}
              minRows={2}
              maxRows={5}
              onChange={(e) =>
                setLander(
                  produce(lander, (draft) => {
                    draft.description = e.target.value;
                  }),
                )
              }
            />
            <Divider />
            <Typography variant="subtitle1">Advanced Settings</Typography>
            <Typography variant="body2">
              Choose additional settings for your landing page.
            </Typography>
            <Stack>
              <FormControlLabel
                componentsProps={{
                  typography: {
                    variant: "body1",
                  },
                }}
                control={
                  <Switch
                    checked={lander.noIndex}
                    onChange={(e, checked) => {
                      setLander(
                        produce(lander, (draft) => {
                          draft.noIndex = checked;
                        }),
                      );
                    }}
                  />
                }
                label="Add no-index"
              />
              <Typography variant="body2">
                If enabled, we'll add the 'no-index' tag to your page,
                preventing search engines like Google from indexing this page.
              </Typography>
            </Stack>
            <TextValidator
              label="Canonical"
              size="small"
              helperText="Add a canonical record to specify the preferred URL for a webpage, assisting in preventing duplicate content issues by signaling the primary version for search engines to index."
              fullWidth
              value={lander.canonical || ""}
              name="canonical"
              validators={["isDomain"]}
              errorMessages={["Must be a valid URL"]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLander(
                  produce(lander, (draft) => {
                    draft.canonical = e.target.value;
                  }),
                );
              }}
            />
            <Stack>
              <FormControlLabel
                componentsProps={{
                  typography: {
                    variant: "body1",
                  },
                }}
                control={
                  <Switch
                    checked={
                      typeof lander.deferIncendiumjs !== "undefined"
                        ? lander.deferIncendiumjs
                        : true
                    }
                    onChange={(e, checked) => {
                      setLander(
                        produce(lander, (draft) => {
                          draft.deferIncendiumjs = checked;
                        }),
                      );
                    }}
                  />
                }
                label="Defer Incendium.js"
              />
              <Typography variant="body2">
                Enabling 'defer' instructs the browser to commence downloading
                the incendium.js script immediately while delaying its execution
                until after the webpage's rendering, potentially enhancing
                loading efficiency.
              </Typography>
            </Stack>
            <Divider />
            <Button variant="contained" color="primary" type="submit" fullWidth>
              Save Settings
            </Button>
          </Stack>
        </AccordianChartBuilderSidebarBlock>

        <AccordianChartBuilderSidebarBlock
          title="Smart Links"
          subTitle="Link this lander to ads, ad sets, or campaigns"
          defaultClosed
        >
          <SmartLinksSection project={project} lander={lander} />
        </AccordianChartBuilderSidebarBlock>

        <AccordianChartBuilderSidebarBlock
          title="Location Setup"
          subTitle="Create a location for this lander."
          defaultClosed
        >
          <Stack mb={4}>
            <Typography variant="subtitle2" color={"secondary"} gutterBottom>
              We have found that this lander would match with the following
              locations.
            </Typography>
            {[...matchingLocations]
              .sort(
                (a, b) =>
                  (a.createdAt || new Date()).getTime() -
                  (b.createdAt || new Date()).getTime(),
              )
              .map((l) => (
                <Typography key={l.id} variant="body2">
                  <strong>{l.name}</strong> - (
                  {l.showInAnalytics
                    ? "show in analytics"
                    : "hide in analytics"}
                  )
                </Typography>
              ))}
          </Stack>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Create new location"
              helperText="Generate a new location with rules designed specifically for this Lander."
              size="small"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />
            <Button fullWidth size="small" onClick={createLocation}>
              Create Location
            </Button>
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      </Suspense>
    </ValidatorForm>
  );
}

export default LightningLanderForm;
